import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "@/shared/components/editor/editor";
import {
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  ChangeOfferDocumentProfileRequest,
  Offer,
} from "@/services/backend/qpm/offers/types/offer";
import {
  ChangeMode,
  ConfigOrigin,
  OfferType,
  SectionMode,
} from "@/services/backend/qpm/offers/types/offer-enum";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";

const defaultIoTRentalServiceText =
  "<ul><li>Ersatz defekter Komponenten innerhalb der ersten 1.800 Aktivierungen pro Falle (ausgenommen fahrlässige und gewaltsame Zerstörung)</li><li>kostenloser telefonischer Support während unserer Servicezeiten</li><li>Schulung Ihres Servicepersonals zum Leeren und Spannen der Fallen</li><li>Schulung für das Webportal</li><li>Schulung zur Sachkunde zum Töten von Wirbeltieren gemäß § 4 Tierschutzgesetz für zwei Beschäftigte</li></ul>";

function toMode(origin: ConfigOrigin): ChangeMode {
  return origin === ConfigOrigin.Manual
    ? ChangeMode.Manual
    : ChangeMode.Automatic;
}

function toConfigPatch<T extends string | number>(value: T, mode: ChangeMode) {
  if (mode === ChangeMode.Automatic) {
    return { mode: ChangeMode.Automatic };
  }

  return { mode: ChangeMode.Manual, value };
}

function buildRequest({
  offerId,
  isExtensionOffer,
  ioTRentalServiceText,
  ioTRentalServiceTextMode,
}: {
  offerId: string;
  isExtensionOffer: boolean;
  ioTRentalServiceText: string;
  ioTRentalServiceTextMode: ChangeMode;
}): ChangeOfferDocumentProfileRequest {
  const documentProfile: ChangeOfferDocumentProfileRequest["documentProfile"] =
    {};
  if (isExtensionOffer) {
    documentProfile.ioTRentalServiceText = toConfigPatch(
      ioTRentalServiceText,
      ioTRentalServiceTextMode,
    );
  }

  return {
    offerId,
    documentProfile,
  };
}

function HtmlEditor({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  const targetValue = value?.trim() ? value : "<p></p>";
  const editor = useEditor(
    {
      content: value,
      editable: !disabled,
      onUpdate: ({ editor: currentEditor }) => {
        onChange(editorGenerateHTML(currentEditor));
      },
    },
    [disabled],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentHTML = editorGenerateHTML(editor).trim() || "<p></p>";
    if (currentHTML === targetValue) {
      return;
    }
    if (!disabled && editor.isFocused) {
      return;
    }
    editor.commands.setContent(targetValue, { emitUpdate: false });
  }, [disabled, editor, targetValue]);

  if (!editor) {
    return <Input value={value} disabled readOnly />;
  }

  return (
    <Editor editor={editor}>
      <EditorToolbar>
        <EditorBoldItalicUnderlineGroup />
        <EditorToolbarSeparator />
        <EditorBulletList />
        <EditorOrderedList />
      </EditorToolbar>
      <EditorContent />
    </Editor>
  );
}

export function OfferIoTRentalServiceTextEditor({
  offer,
  disabledByPriceVisibility = false,
}: {
  offer: Offer;
  disabledByPriceVisibility?: boolean;
}) {
  const { setValidationError } = useOfferContext();
  const isExtensionOffer =
    offer.documentProfile.offerType.value === OfferType.Extension;

  const [
    changeDocumentProfile,
    {
      error: changeDocumentProfileError,
      isLoading: isSavingDocumentProfile,
      isSuccess: isChangeDocumentProfileSuccess,
      reset: resetChangeDocumentProfile,
    },
  ] = useQpmOfferChangeDocumentProfileMutation();
  const [reloadSnapshot] = useQpmOfferReloadOfferConfigSnapshotMutation();

  const [ioTRentalServiceText, setIoTRentalServiceText] = useState<string>(
    offer.documentProfile.extension?.ioTRentalServiceText?.value ??
      defaultIoTRentalServiceText,
  );
  const [ioTRentalServiceTextMode, setIoTRentalServiceTextMode] =
    useState<ChangeMode>(
      toMode(
        offer.documentProfile.extension?.ioTRentalServiceText?.origin ??
          ConfigOrigin.Derived,
      ),
    );

  const [savedRequest, setSavedRequest] =
    useState<ChangeOfferDocumentProfileRequest | null>(null);
  const initializedOfferId = useRef<string | null>(null);
  const lastSubmittedRequest = useRef<ChangeOfferDocumentProfileRequest | null>(
    null,
  );

  useEffect(() => {
    if (initializedOfferId.current === offer.id) {
      return;
    }

    initializedOfferId.current = offer.id;
    const initialText =
      offer.documentProfile.extension?.ioTRentalServiceText?.value ??
      defaultIoTRentalServiceText;
    const initialMode = toMode(
      offer.documentProfile.extension?.ioTRentalServiceText?.origin ??
        ConfigOrigin.Derived,
    );

    setIoTRentalServiceText(initialText);
    setIoTRentalServiceTextMode(initialMode);
    setSavedRequest(
      buildRequest({
        offerId: offer.id,
        isExtensionOffer,
        ioTRentalServiceText: initialText,
        ioTRentalServiceTextMode: initialMode,
      }),
    );
  }, [isExtensionOffer, offer]);

  const requestDraft = useMemo(
    () =>
      buildRequest({
        offerId: offer.id,
        isExtensionOffer,
        ioTRentalServiceText,
        ioTRentalServiceTextMode,
      }),
    [
      offer.id,
      isExtensionOffer,
      ioTRentalServiceText,
      ioTRentalServiceTextMode,
    ],
  );

  const hasPendingChanges = useMemo(() => {
    if (!savedRequest) {
      return false;
    }

    return JSON.stringify(savedRequest) !== JSON.stringify(requestDraft);
  }, [requestDraft, savedRequest]);

  const onDebouncedChangeDocumentProfile = useCallback(
    (nextRequest: ChangeOfferDocumentProfileRequest) => {
      lastSubmittedRequest.current = nextRequest;
      changeDocumentProfile(nextRequest);
    },
    [changeDocumentProfile],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    requestDraft,
    onDebouncedChangeDocumentProfile,
    isSavingDocumentProfile,
    changeDocumentProfileError,
    isChangeDocumentProfileSuccess,
    resetChangeDocumentProfile,
    500,
    {
      skip: !hasPendingChanges || offer.immutable,
      toastError: true,
    },
  );

  useEffect(() => {
    if (
      !changeDocumentProfileError ||
      !rtkErrIsValidationError(changeDocumentProfileError)
    ) {
      return;
    }

    setValidationError(changeDocumentProfileError.data as ValidationError);
  }, [changeDocumentProfileError, setValidationError]);

  useEffect(() => {
    if (!isChangeDocumentProfileSuccess || !lastSubmittedRequest.current) {
      return;
    }

    setSavedRequest(lastSubmittedRequest.current);
    reloadSnapshot({ offerId: offer.id });
    setValidationError(null);
  }, [
    isChangeDocumentProfileSuccess,
    offer.id,
    reloadSnapshot,
    setValidationError,
  ]);

  const hasIoTMonitors = Object.values(offer.monitorSnaps ?? {}).some(
    (snap) => snap.type === MonitorType.IoT,
  );
  const hasIoTRentalContract =
    offer.documentProfile.extension?.ioTRentalContractMode?.value ===
    SectionMode.Include;
  const disabled =
    offer.immutable ||
    !isExtensionOffer ||
    !hasIoTMonitors ||
    !hasIoTRentalContract ||
    disabledByPriceVisibility;

  return (
    <div
      className={`space-y-2 ${disabledByPriceVisibility ? "opacity-50" : ""}`}
    >
      <div className="flex items-center gap-2">
        <Label className="min-h-5 text-sm">
          {t("Serviceleistungen (IoT-Mietvertrag)")}
        </Label>
        {disabledByPriceVisibility ? (
          <InfoIconTooltip
            text={t(
              "Diese Inhalte sind im PDF ausgeblendet, da die zugehörige Preisposition in den Preispositionen deaktiviert wurde.",
            )}
          />
        ) : null}
      </div>
      <HtmlEditor
        value={ioTRentalServiceText}
        disabled={disabled}
        onChange={(next) => {
          setIoTRentalServiceText((current) =>
            current === next ? current : next,
          );
          setIoTRentalServiceTextMode((current) =>
            current === ChangeMode.Manual ? current : ChangeMode.Manual,
          );
        }}
      />
      <RTKQueryErrorAlert error={changeDocumentProfileError} />
    </div>
  );
}
